import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StatGroup, StatTile } from '@/components/ui/stat-tile';
import { formatCurrency, formatNumber } from '@/lib/format';
import { useVoucherReport } from './vouchers.api';

/**
 * The voucher money figures, from `GET v2/admin/vouchers/report`.
 *
 * One component for the Gift Vouchers report and the Business Overview's
 * Money tab, so the two can never show different numbers for the same range.
 * The overview gets the `compact` single row; the report gets it grouped by
 * question — what sold, what it paid for, and what is still owed.
 *
 * Sales are dated by payment, redemptions by the class date, and the
 * outstanding figures are a position as of today whatever the range.
 */
export function VoucherReportTiles({
  from,
  to,
  compact = false,
  enabled = true,
}: {
  /** API instants, as `toApiDate` produces them. */
  from: string;
  to: string;
  compact?: boolean;
  enabled?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const report = useVoucherReport(from, to, enabled);
  const data = report.data;
  const loading = report.isPending;
  const busy = report.isFetching && !report.isPending;
  const openList = () => navigate('/booking/manage/vouchers');

  const sales = (
    <StatTile
      loading={loading}
      busy={busy}
      tone="primary"
      label={t('vouchers.report.salesNet')}
      value={formatCurrency(data?.salesNet)}
      sub={t('vouchers.report.soldSub', {
        count: data?.sold ?? 0,
        refunded: formatCurrency(data?.refunded),
      })}
      tooltip={t('vouchers.report.salesNetTooltip')}
      onClick={compact ? openList : undefined}
    />
  );
  const redeemed = (
    <StatTile
      loading={loading}
      busy={busy}
      tone="good"
      label={t('vouchers.report.redeemedValue')}
      value={formatCurrency(data?.redeemedValue)}
      sub={t('vouchers.report.redemptionsSub', { count: data?.redemptions ?? 0 })}
      tooltip={t('vouchers.report.redeemedValueTooltip')}
    />
  );
  const outstanding = (
    <StatTile
      loading={loading}
      busy={busy}
      tone="warn"
      label={t('vouchers.report.outstandingValue')}
      value={formatCurrency(data?.outstandingValue)}
      sub={t('vouchers.report.outstandingSub', {
        count: data?.outstandingUses ?? 0,
      })}
      tooltip={t('vouchers.report.outstandingValueTooltip')}
    />
  );
  const expired = (
    <StatTile
      loading={loading}
      busy={busy}
      label={t('vouchers.report.expiredUnused')}
      value={formatCurrency(data?.expiredUnusedValue)}
      tooltip={t('vouchers.report.expiredUnusedTooltip')}
    />
  );

  if (compact) {
    return (
      <StatGroup title={t('vouchers.report.overviewGroup')}>
        {sales}
        {redeemed}
        {outstanding}
        {expired}
      </StatGroup>
    );
  }

  return (
    <div className="space-y-5">
      <StatGroup title={t('vouchers.report.salesGroup')}>
        {sales}
        <StatTile
          loading={loading}
          busy={busy}
          label={t('vouchers.report.collected')}
          value={formatCurrency(data?.salesCollected)}
          sub={t('vouchers.report.vouchersSold', { count: data?.sold ?? 0 })}
        />
        <StatTile
          loading={loading}
          busy={busy}
          tone="warn"
          label={t('vouchers.report.refunded')}
          value={formatCurrency(data?.refunded)}
        />
        <StatTile
          loading={loading}
          busy={busy}
          label={t('vouchers.report.complimentary')}
          value={formatNumber(data?.complimentaryIssued)}
          tooltip={t('vouchers.report.complimentaryTooltip')}
        />
      </StatGroup>

      <StatGroup title={t('vouchers.report.usageGroup')}>
        {redeemed}
        <StatTile
          loading={loading}
          busy={busy}
          label={t('vouchers.report.redemptions')}
          value={formatNumber(data?.redemptions)}
        />
      </StatGroup>

      <StatGroup title={t('vouchers.report.liabilityGroup')}>
        {outstanding}
        <StatTile
          loading={loading}
          busy={busy}
          label={t('vouchers.report.outstandingUses')}
          value={formatNumber(data?.outstandingUses)}
        />
        {expired}
      </StatGroup>
    </div>
  );
}
